import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";

import { cn } from "../../utils/tailwindUtils";

const MAX_QUERY_LENGTH = 1000; // matches ChatRequestSerializer on the backend

/**
 * Question input. Grows to a few lines as the user types; Enter sends and
 * Shift+Enter inserts a newline, which is what people expect from a chat box.
 */
const CopilotComposer = ({
    onSend,
    disabled,
    placeholder = "Ask about leave, payslips, attendance or holidays…",
    compact = false,
    autoFocus = false,
}) => {
    const [value, setValue] = useState("");
    const textareaRef = useRef(null);

    // Keep the height in step with the content, capped so the thread stays visible.
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, compact ? 88 : 128)}px`;
    }, [value, compact]);

    useEffect(() => {
        if (autoFocus) textareaRef.current?.focus();
    }, [autoFocus]);

    const submit = () => {
        const query = value.trim();
        if (!query || disabled) return;
        setValue("");
        onSend(query);
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
        }
    };

    return (
        <form
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
            className={cn(
                "flex items-end gap-2 rounded-xl border border-input bg-background",
                "focus-within:border-sidebar-primary focus-within:ring-1 focus-within:ring-sidebar",
                compact ? "px-2.5 py-2" : "px-3 py-2.5"
            )}
        >
            <textarea
                ref={textareaRef}
                rows={1}
                value={value}
                maxLength={MAX_QUERY_LENGTH}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className={cn(
                    "flex-1 resize-none bg-transparent leading-relaxed text-foreground outline-none",
                    "placeholder:text-muted-foreground",
                    compact ? "text-xs" : "text-sm"
                )}
            />
            <button
                type="submit"
                disabled={disabled || !value.trim()}
                title="Send"
                className={cn(
                    "flex shrink-0 items-center justify-center rounded-lg transition-colors",
                    compact ? "h-7 w-7" : "h-8 w-8",
                    disabled || !value.trim()
                        ? "cursor-not-allowed bg-muted text-muted-foreground"
                        : "bg-sidebar text-white hover:bg-sidebar-primary"
                )}
            >
                <Send className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
            </button>
        </form>
    );
};

export default CopilotComposer;
